import { FC, Suspense, useEffect, useState } from 'react';
import { StudentSkillStates } from '../models/StudentSkillState';
import { getDiagnosticInformation } from '../graphQLOperations/Dashboard';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { MainFallback } from '../components/Fallback/MainFallBack';
import { useLazyLoadQuery } from 'react-relay';
import { selectedTargetGroup, setTargetGroup } from '../redux/reducers/targetGroupSlice';
import { DashboardDiagnosticQuery } from '../graphQLOperations/__generated__/DashboardDiagnosticQuery.graphql';
import { selectedAccessToken } from '../redux/reducers/accessTokenSlice';
import { useAuth0 } from '@auth0/auth0-react';
import SkillStateUpdateLoader from '../components/SkillStateUpdateLoader/SkillStateUpdateLoader';
import { Unit } from '../models/Unit';
import { selectedUnit } from '../redux/reducers/unitSlice';
import { updateSSSWithIcons } from '../businessLogicService/addIconsToCards';
import { StudentTour } from '../models/StudentTour';
import { selectedFilter } from '../redux/reducers/filterSlice';
import { selectedSuggestedView } from '../redux/reducers/suggestedViewSlice';
import { sortStudentSkillStatesByLabel } from '../businessLogicService/sortCriteria';
import { checkTargetGroupMatch } from '../businessLogicService/matchCurrentTargetGroupInSites';
import DashboardHeader from '../components/Dashboard/DashboardHeader';
import { setStudentSkills } from '../redux/reducers/studentSkillsSlice';
import { TargetGroup } from '../models/TargetGroup';
import UnitPageContainer from '../components/Dashboard/UnitPageContainer';
import { StudentFocusPublisher } from '../components/UnitFocus/StudentFocusPublisher';

interface Props {
  isInferred: boolean;
  setIsInferred: (i: boolean) => void;
}

const DashboardUnitsSelector: FC<Props> = ({ isInferred, setIsInferred }) => {
  const { isAuthenticated } = useAuth0();

  const dispatch = useAppDispatch();
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const userRedux = useAppSelector(selectUser);
  const filterRedux = useAppSelector(selectedFilter);
  const suggestedViewMode = useAppSelector(selectedSuggestedView);
  const accessTokenRedux = useAppSelector(selectedAccessToken);
  const [studentSkillStates, setStudentSkillStates] = useState<StudentSkillStates[]>();
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [waitToRender, setWaitToRender] = useState(true);
  const [isCurrentTargetGroupInSite, setIsCurrentTargetGroupInSite] = useState(true);

  const unitRedux = useAppSelector(selectedUnit);
  const { latestCompletedStudentSkillDiagnostic, currentTargetGroupByStudentId } =
    useLazyLoadQuery<DashboardDiagnosticQuery>(
      getDiagnosticInformation,
      {
        skip: !isAuthenticated || !accessTokenRedux,
        studentId: `StudentNode:${userRedux?.id}`,
      },
      { fetchKey: accessTokenRedux || '', fetchPolicy: 'network-only' },
    );

  const currentTargets = (currentTargetGroupByStudentId?.targetGroup?.targets ?? []).filter(
    (v): v is NonNullable<typeof v> => v != null,
  );
  useEffect(() => {
    const diagnosticFinished = localStorage.getItem('diagnostic_finished');
    if (diagnosticFinished) {
      if (diagnosticFinished !== 'true') setShowDiagnostic(true);
    } else {
      if (!latestCompletedStudentSkillDiagnostic || latestCompletedStudentSkillDiagnostic?.retakeAssigned) {
        setShowDiagnostic(true);
      }
    }

    if (latestCompletedStudentSkillDiagnostic?.isInferred && !latestCompletedStudentSkillDiagnostic.retakeAssigned) {
      setIsInferred(true);
      localStorage.removeItem('diagnostic_finished');
    }
    setWaitToRender(false);
  }, [latestCompletedStudentSkillDiagnostic]);

  useEffect(() => {
    if (currentTargetGroupByStudentId && !targetGroupRedux) {
      setIsCurrentTargetGroupInSite(
        checkTargetGroupMatch(targetGroupsNodes, currentTargetGroupByStudentId?.targetGroup as TargetGroup),
      );
      dispatch(setTargetGroup(currentTargetGroupByStudentId?.targetGroup as TargetGroup));
    }
  }, [currentTargetGroupByStudentId]);

  useEffect(() => {
    if (targetGroupRedux && studentSkillStates) {
      // Filter skills to only include those from the current target group (grade)
      // This is the key fix - we need to pre-filter by target group
      const skillsForCurrentTargetGroup = studentSkillStates.filter(
        skill =>
          skill.skill?.targetSkills?.some(targetSkill => targetSkill.target?.targetGroup?.id === targetGroupRedux.id),
      );

      let sss = sortStudentSkillStatesByLabel(skillsForCurrentTargetGroup, filterRedux.sortedBy.value);

      sss = updateSSSWithIcons(
        skillsForCurrentTargetGroup,
        StudentTour.ALL_SKILLS,
        targetGroupRedux,
        unitRedux as Unit,
        true,
      );

      // Set the filtered skills for the progress wheel
      dispatch(setStudentSkills(sss));
    }
  }, [studentSkillStates, suggestedViewMode, unitRedux, targetGroupRedux]);

  const targetGroupsNodes =
    userRedux?.sites
      ?.map(
        site =>
          site?.targetGroups?.map(target => ({
            id: target?.id || '',
            name: target?.name || '',
          })) ?? [],
      )
      ?.flat() ?? [];

  useEffect(() => {
    const tgName = targetGroupRedux?.name?.trim();
    document.title = tgName ? `Teach to One Roadmaps | All Units` : 'Teach to One Roadmaps | Dashboard';
  }, [targetGroupRedux?.name]);

  return (
    <div className="grid nc_content-frame w-full gap-0">
      {!waitToRender && (
        <>
          {!showDiagnostic && (
            <Suspense fallback={<MainFallback />}>
              {isInferred ? (
                <>
                  {targetGroupRedux && (
                    <DashboardHeader
                      allTargetsGroup={targetGroupsNodes as TargetGroup[]}
                      setStudentSkillStates={setStudentSkillStates}
                      setSSSWithoutExtension={() => {}}
                      currentTargets={currentTargets}
                    />
                  )}
                  {targetGroupRedux?.id && isCurrentTargetGroupInSite && studentSkillStates ? (
                    <>
                      <Suspense fallback={null}>
                        <StudentFocusPublisher currentTargets={currentTargets} fetchKey={accessTokenRedux || ''} />
                      </Suspense>
                      <UnitPageContainer currentTargets={currentTargets} />
                    </>
                  ) : null}
                </>
              ) : (
                <SkillStateUpdateLoader />
              )}
            </Suspense>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardUnitsSelector;
